import React, { useState, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import pluralize from 'pluralize';
import ExpandableButton from 'common/buttons/ExpandableButton';
import formatSpend from 'common/utils/Money/formatSpend';
import { BaseLineItem } from './LineItems';
import AuthorizationSpendCard from '../../components/AuthorizationSpendCard';

const getExpandableButtonDataTestId = (isVoided, isAuthorized) => {
  if (isVoided) {
    return 'expandable-button-voided';
  } if (isAuthorized) {
    return 'expandable-button-authorized';
  }
  return 'expandable-button-not-yet-authorized';
};

const AuthorizationSpendExpandable = ({
  title,
  items,
  totalSpend,
  unit,
  isAuthorized,
  isVoided,
  isCostBased,
  currentAuthorizationId,
  lineageItems,
  showUpdatePending,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const onClick = useCallback(() => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  }, []);

  const formattedTotalSpend = formatSpend(totalSpend, unit, isCostBased);
  const requestCount = `${items.length} ${pluralize('request', items.length)}`;
  const lineageCount = lineageItems?.length || 0;

  if (items.length === 0 && lineageCount === 0) {
    return (
      <BaseLineItem field={title}>
        No Requests
      </BaseLineItem>
    );
  }
  const expandableButtonDataTestId = getExpandableButtonDataTestId(isVoided, isAuthorized);

  return (
    <>
      <BaseLineItem field={title}>
        <div className="flex">
          {showUpdatePending && lineageCount > 0 ? (
            <span>Update pending ({lineageCount} {pluralize('request', lineageCount)})</span>
          ) : (
            <span>{isVoided ? requestCount : <>{formattedTotalSpend} ({requestCount})</>}</span>
          )}
          <ExpandableButton
            isOpen={isOpen}
            onClick={onClick}
            dataTestId={expandableButtonDataTestId}
            className="ml-3"
          />
        </div>
      </BaseLineItem>
      {isOpen && (
        <div className="py-4 space-y-4 col-span-2">
          {showUpdatePending && lineageItems && lineageItems.map((item) => (
            <AuthorizationSpendCard
              key={item.id}
              item={item}
              unit={unit}
              isAuthorized={isAuthorized}
              isCostBased={isCostBased}
              isCurrentItem={false}
              enabledLink
              isLineageItem
            />
          ))}
          {items.map((item) => (
            <AuthorizationSpendCard
              key={item.id}
              item={item}
              unit={unit}
              isAuthorized={isAuthorized}
              isVoided={isVoided}
              isCostBased={isCostBased}
              isCurrentItem={currentAuthorizationId === item.id}
              enabledLink
            />
          ))}
        </div>
      )}
    </>
  );
};

AuthorizationSpendExpandable.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      short_id: PropTypes.string.isRequired,
      state: PropTypes.string.isRequired,
      spend: PropTypes.number.isRequired,
      service_starts_at: PropTypes.string.isRequired,
      service_ends_at: PropTypes.string.isRequired,
      updated_at: PropTypes.string.isRequired,
      created_at: PropTypes.string.isRequired,
      requester_name: PropTypes.string.isRequired,
      case_state: PropTypes.string.isRequired,
      case_opened_date: PropTypes.string,
    }).isRequired,
  ).isRequired,
  totalSpend: PropTypes.number,
  unit: PropTypes.string,
  isAuthorized: PropTypes.bool,
  isVoided: PropTypes.bool,
  isCostBased: PropTypes.bool.isRequired,
  currentAuthorizationId: PropTypes.string.isRequired,
  lineageItems: PropTypes.array,
  showUpdatePending: PropTypes.bool,
};

AuthorizationSpendExpandable.defaultProps = {
  isAuthorized: false,
  isVoided: false,
  unit: '',
  lineageItems: [],
  showUpdatePending: false,
  totalSpend: 0,
};

export default AuthorizationSpendExpandable;
